/* ============================================================================
   We Feed You — custom tax invoice for the Custlo account dashboard
   Install as: assets/wfy-invoice.js
   Load once, before </body> in layout/theme.liquid:
     <script src="{{ 'wfy-invoice.js' | asset_url }}" defer></script>
   Also add (for authoritative GST/discount):  {% render 'wfy-invoice-data' %}

   WHAT IT DOES
   - Intercepts Custlo's "Download invoice" button (.custlo-t1-download-invoice-feature),
     stops its broken jsPDF export, and opens OUR clean, text-based tax invoice to print / save PDF.
   - Reads the order from window.customer_data (line items, total, addresses, dates, tags).
   - Merges the REAL Shopify GST + discount + delivery from the #wfy-invoice-auth JSON
     (snippets/wfy-invoice-data.liquid). Falls back to GST=total/11 + derived discount if absent.
   - Tag-gated: customers tagged "Self-managed" get the NDIS claim invoice (30/70 breakdown);
     everyone else gets the standard invoice.
   ============================================================================ */
(function () {
  "use strict";

  var BIZ = {
    name: "We Feed You Pty Ltd",
    abn: "84 614 310 100",
    addr: ["Kitchen 1, Unit 3", "107–109 Whitehall Street", "Footscray VIC 3011", "Australia"],
    email: "team@wefeedyou.com.au",
    web: "wefeedyou.com.au",
    phone: "1300 853 239"
  };
  var NDIS_TAG = "self-managed";           // the tag that switches to the NDIS claim invoice
  var LOGO = "https://cdn.shopify.com/s/files/1/1226/3982/files/We_Feed_You_translucent_logo_257dad.png?v=1754374386";

  // ---- helpers ----------------------------------------------------------------
  function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
  function money(n) { return "$" + round2(n).toFixed(2); }
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
  function num(v) { // "A$14.95" | "14.95" | 14.95 -> 14.95
    if (v == null) return 0;
    if (typeof v === "number") return v;
    var m = String(v).replace(/[^0-9.\-]/g, "");
    return m === "" ? 0 : parseFloat(m);
  }

  // ---- data -------------------------------------------------------------------
  function customer() {
    var cd = window.customer_data || {};
    return cd.customer || cd || {};
  }
  function isSelfManaged() {
    var tags = (customer().tags || []).map(function (t) { return String(t).toLowerCase().trim(); });
    return tags.indexOf(NDIS_TAG) !== -1;
  }
  function authMap() {
    var map = {};
    try {
      var el = document.getElementById("wfy-invoice-auth");
      if (el) (JSON.parse(el.textContent) || []).forEach(function (o) { if (o && o.name) map[o.name] = o; });
    } catch (e) { /* fall back silently */ }
    return map;
  }
  function findOrder(name, id) {
    var orders = (customer().orders) || [];
    for (var i = 0; i < orders.length; i++) {
      var o = orders[i];
      if (name && o.name === name) return o;
      if (id && String(o.id) === String(id)) return o;
    }
    return null;
  }
  function fmtAddress(a) {
    if (!a) return "";
    var parts = [a.name, a.address1, a.address2,
      [a.city, a.province_code || a.province, a.zip].filter(Boolean).join(" "), a.country];
    return parts.filter(function (p) { return p && String(p).trim(); }).map(esc).join("<br>");
  }

  // ---- compute the invoice model ---------------------------------------------
  function model(order, auth) {
    var items = (order.line_items || []).map(function (li) {
      return { title: li.title || li.name, qty: Number(li.quantity) || 0,
        unit: num(li.price != null ? li.price : li.price_formatted),
        amount: num(li.final_line_price != null ? li.final_line_price : li.line_price) };
    });
    var lineSum = round2(items.reduce(function (s, i) { return s + i.amount; }, 0));
    var total = num(order.current_total_price != null ? order.current_total_price : order.total_price_formatted);

    // authoritative figures (cents) when the Liquid snippet is present
    var hasAuth = auth && auth.total != null;
    var discount = hasAuth ? round2(num(auth.discount) / 100) : Math.max(0, round2(lineSum - total));
    var shipping = hasAuth ? round2(num(auth.shipping) / 100) : 0;
    var gst;
    if (hasAuth && num(auth.tax) > 0) gst = round2(num(auth.tax) / 100);         // real Shopify GST
    else gst = round2(total / 11);                                                // fallback: 1/11 of GST-incl total
    // If auth gave a total, trust it; else use window total.
    if (hasAuth) total = round2(num(auth.total) / 100);

    var meals = round2(lineSum - discount);           // discounted meal value (excl delivery)
    var ing30 = round2(meals * 0.30);                 // NDIS: ingredients co-payment
    var claim70 = round2(total - ing30);              // NDIS: 70% of meals + delivery (claimable)

    var paid = /paid|partially_paid/i.test(order.financial_status || "") && !/refunded/i.test(order.financial_status || "");
    return {
      no: order.name, date: order.created_at_long || order.created_at_formatted || "",
      statusLabel: (order.financial_status || "").replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); }),
      paid: paid,
      bill: order.billing_address || order.shipping_address,
      items: items, lineSum: lineSum, discount: discount, shipping: shipping,
      gst: gst, total: total, ing30: ing30, claim70: claim70, hasDelivery: shipping > 0
    };
  }

  // ---- render -----------------------------------------------------------------
  function invoiceHTML(m, ndis) {
    var rows = m.items.map(function (i) {
      return '<tr><td class="l">' + esc(i.title) + '</td><td>' + i.qty + '</td><td>' +
        money(i.unit) + '</td><td>' + money(i.amount) + '</td></tr>';
    }).join("");
    if (m.hasDelivery) rows += '<tr><td class="l">Delivery</td><td>1</td><td>' + money(m.shipping) + '</td><td>' + money(m.shipping) + '</td></tr>';
    if (ndis) {
      rows += '<tr class="info"><td class="l">Cost of Ingredients (30% of meal price) = ' + money(m.ing30) + '</td><td></td><td></td><td></td></tr>';
      rows += '<tr class="info"><td class="l">Cost of Preparation (70% of meal price)' + (m.hasDelivery ? ' &amp; Delivery' : '') + ' = ' + money(m.claim70) + '</td><td></td><td></td><td></td></tr>';
    }
    var discRow = m.discount > 0 ? '<div class="r disc"><span>Discount</span><b>−' + money(m.discount) + '</b></div>' : '';
    var paidRows = m.paid ? '<div class="r"><span>Amount paid</span><b class="paidoff">−' + money(m.total) + '</b></div><div class="r"><span>Amount due (AUD)</span><b>$0.00</b></div>' : '';
    var claimNote = '';  // breakdown is shown as line items above (per Susannah); no extra box

    return '<!doctype html><html lang="en"><head><meta charset="utf-8">' +
      '<title>We Feed You — Tax Invoice ' + esc(m.no) + '</title>' +
      '<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>' +
      '<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">' +
      '<style>' + CSS + '</style></head><body onload="window.focus();window.print();">' +
      '<div class="bar"><button onclick="window.print()">Print / Save PDF</button></div>' +
      '<div class="sheet"><div class="top"><img class="logo" src="' + LOGO + '" alt="We Feed You">' +
      '<div class="ht"><h1>TAX INVOICE</h1></div></div>' +
      '<div class="grid"><div class="col"><p class="lbl">From</p><p class="name">' + esc(BIZ.name) + '</p>' +
      '<div>ABN ' + esc(BIZ.abn) + '<br>' + BIZ.addr.map(esc).join('<br>') + '</div></div>' +
      '<div class="col"><p class="lbl">Bill to</p><div class="name">' + (fmtAddress(m.bill) || esc((customer().first_name||'') + ' ' + (customer().last_name||''))) + '</div></div>' +
      '<div class="col meta"><p class="lbl">Details</p><div class="r"><span>Invoice no.</span><b>' + esc(m.no) + '</b></div>' +
      '<div class="r"><span>Date</span><b>' + esc(m.date) + '</b></div>' +
      '<div class="r"><span>Status</span><span class="tag ' + (m.paid ? 'paid' : 'other') + '">' + esc(m.statusLabel) + '</span></div></div></div>' +
      '<table><thead><tr><th class="l">Description</th><th>Qty</th><th>Unit price</th><th>Amount AUD</th></tr></thead><tbody>' + rows + '</tbody></table>' +
      '<div class="totrow"><div class="tot"><div class="r"><span>Subtotal (meals)</span><b>' + money(m.lineSum) + '</b></div>' +
      discRow + (m.hasDelivery ? '<div class="r"><span>Delivery</span><b>' + money(m.shipping) + '</b></div>' : '') +
      '<div class="r gst"><span>Includes GST</span><b>' + money(m.gst) + '</b></div>' +
      '<div class="r grand"><span>Total (AUD)</span><b>' + money(m.total) + '</b></div>' + paidRows + '</div></div>' +
      (m.paid ? '<div class="stamp">This invoice has been paid in full.</div>' : '') + claimNote +
      '<div class="foot"><div><span class="s">' + esc(BIZ.name) + '</span> · ABN ' + esc(BIZ.abn) + '<br>' +
      esc(BIZ.addr.slice(0, 2).join(', ')) + ', ' + esc(BIZ.addr[2]) + '<br>This document is a tax invoice for GST purposes.</div>' +
      '<div style="text-align:right"><span class="s">Contact</span><br>' + esc(BIZ.email) + '<br>' + esc(BIZ.phone) + ' · ' + esc(BIZ.web) + '</div></div>' +
      '</div></body></html>';
  }

  var CSS =
    ":root{--blue:#257DAD;--navy:#12133D;--ink:#2b2c4e;--muted:#7c7e9c;--line:#e3e5ef;--soft:#f5f7fb;--orange:#d73f09;--good:#2D9B7F}" +
    "*{box-sizing:border-box}html,body{margin:0;padding:0}" +
    "body{font-family:'Poppins',-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:var(--ink);background:#e9ebf2;font-size:14px}" +
    ".bar{max-width:820px;margin:16px auto 0;text-align:right}.bar button{font-family:inherit;font-weight:600;font-size:14px;border:0;border-radius:8px;padding:10px 18px;cursor:pointer;background:var(--blue);color:#fff}" +
    ".sheet{max-width:820px;margin:12px auto 40px;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 10px 40px rgba(18,19,61,.10);padding:38px 42px 30px}" +
    ".top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px}" +
    ".logo{width:92px;height:auto;display:block}" +
    ".badge{width:74px;height:74px;border-radius:50%;background:var(--blue);color:#fff;display:flex;flex-direction:column;justify-content:center;align-items:center;line-height:1.02;font-weight:700;font-size:16px;text-align:center}" +
    ".ht{text-align:right}.ht h1{margin:0;font-size:24px;letter-spacing:1.5px;font-weight:700;color:var(--navy)}.ht .sub{margin-top:4px;font-size:12.5px;color:var(--muted);font-weight:500}" +
    ".tag{display:inline-block;margin-top:8px;font-size:10.5px;font-weight:700;letter-spacing:.5px;padding:3px 9px;border-radius:20px;text-transform:uppercase;background:var(--soft);color:var(--muted)}.tag.paid{background:#e4f4ee;color:var(--good)}.tag.other{background:#fdeceb;color:var(--orange)}" +
    ".grid{display:flex;justify-content:space-between;gap:26px;margin-bottom:6px}.col{font-size:12.5px;line-height:1.6}.lbl{font-size:10.5px;letter-spacing:1.3px;text-transform:uppercase;color:var(--muted);font-weight:600;margin:0 0 5px}.name{font-size:14px;font-weight:700;color:var(--navy);margin:0 0 2px}.meta{min-width:190px}.meta .r{display:flex;justify-content:space-between;gap:14px;padding:2px 0}.meta .r span{color:var(--muted)}.meta .r b{color:var(--navy);font-weight:600}" +
    "table{width:100%;border-collapse:collapse;font-size:12.5px;margin-top:16px}thead th{font-size:10.5px;letter-spacing:.8px;text-transform:uppercase;color:var(--muted);font-weight:600;text-align:right;padding:0 0 9px;border-bottom:2px solid var(--navy)}thead th.l{text-align:left}tbody td{padding:10px 0;border-bottom:1px solid var(--line);text-align:right;vertical-align:top}tbody td.l{text-align:left;font-weight:500;color:var(--navy)}tbody tr.info td{color:var(--muted)}tbody tr.info td.l{color:var(--navy);font-weight:500}" +
    ".totrow{display:flex;justify-content:flex-end;margin-top:14px}.tot{width:300px;font-size:13px}.tot .r{display:flex;justify-content:space-between;padding:5px 0}.tot .r span{color:var(--muted)}.tot .r b{color:var(--navy);font-weight:600}.tot .disc b{color:var(--orange)}.tot .gst{border-top:1px solid var(--line);margin-top:4px;padding-top:8px}.tot .grand{border-top:2px solid var(--navy);margin-top:4px;padding-top:10px}.tot .grand span{font-size:15px;color:var(--navy);font-weight:700}.tot .grand b{font-size:18px;color:var(--navy);font-weight:700}.tot .paidoff{color:var(--good);font-weight:600}" +
    ".stamp{text-align:center;font-size:12px;color:var(--good);font-weight:600;margin-top:16px}" +
    ".foot{margin-top:18px;padding-top:14px;border-top:1px solid var(--line);display:flex;justify-content:space-between;gap:20px;font-size:11px;color:var(--muted);line-height:1.5}.foot .s{color:var(--navy);font-weight:600}" +
    "@media print{body{background:#fff}.bar{display:none}.sheet{box-shadow:none;border-radius:0;margin:0;padding:14mm;max-width:100%}@page{size:A4;margin:0}}";

  // ---- open the invoice in a print window -------------------------------------
  function openInvoice(name, id) {
    var order = findOrder(name, id);
    if (!order) { alert("Sorry — we couldn't find that order to build the invoice."); return; }
    var m = model(order, authMap()[order.name]);
    var html = invoiceHTML(m, isSelfManaged());
    var w = window.open("", "_blank");                 // user-gesture: not popup-blocked
    if (!w) { alert("Please allow pop-ups to download your invoice."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  }

  // ---- intercept the Custlo button (capture phase, before Custlo's own handler) ----
  document.addEventListener("click", function (ev) {
    var btn = ev.target && ev.target.closest && ev.target.closest(".custlo-t1-download-invoice-feature");
    if (!btn) return;
    ev.preventDefault(); ev.stopImmediatePropagation();
    openInvoice(btn.getAttribute("data-order-name"), btn.getAttribute("data-order-id"));
  }, true);

  window.WFYInvoice = { open: openInvoice };            // manual hook, e.g. WFYInvoice.open('WFY-HD-82165-1')
})();
